import React, { useState, useEffect } from 'react';
import { useSessions } from '../context/SessionContext';
import { useCommunity } from '../context/CommunityContext';
import { fetchArchivedSessions, fetchPatients, deleteArchivedSession } from '../services/api';

function SessionArchivPage() {
  const { getArchivedSessions, deleteSession } = useSessions();
  const { shareHook, isShared } = useCommunity();
  const localSessions = getArchivedSessions();

  const [backendSessions, setBackendSessions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [patientFilter, setPatientFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteSource, setDeleteSource] = useState(null); // 'local' | 'backend'
  const [backendError, setBackendError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setBackendError('');
    fetchArchivedSessions(patientFilter || null)
      .then(data => { setBackendSessions(data); })
      .catch(e => { setBackendSessions([]); setBackendError(e.message || 'Fehler beim Laden'); });
    fetchPatients().then(setPatients).catch(() => {});
  }, [patientFilter, refreshKey]);

  // Backend-Sessions haben eine numerische id, lokale haben string-id
  const sessions = patientFilter
    ? backendSessions
    : [
        ...backendSessions,
        ...localSessions.filter((ls) => !backendSessions.some((bs) => bs.session_name === ls.name)),
      ];
  const [shareSession, setShareSession] = useState(null);
  const [discussionPoints, setDiscussionPoints] = useState('');

  const formatDate = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const getPreview = (session) => {
    if (session.summary && Array.isArray(session.summary) && session.summary.length > 0) {
      const firstSection = session.summary[0];
      const items = firstSection.items || [];
      return items.slice(0, 3).join(' \u2022 ');
    }
    return 'Keine medizinischen Informationen erfasst';
  };

  const isBackendSession = (session) => typeof session.id === 'number';

  const handleDeleteClick = (e, session) => {
    e.stopPropagation();
    setDeleteConfirmId(session.id);
    setDeleteSource(isBackendSession(session) ? 'backend' : 'local');
  };

  const confirmDelete = () => {
    if (!deleteConfirmId) return;
    if (deleteSource === 'backend') {
      deleteArchivedSession(deleteConfirmId)
        .then(() => setBackendSessions((prev) => prev.filter((s) => s.id !== deleteConfirmId)))
        .catch(() => {});
    } else {
      deleteSession(deleteConfirmId);
    }
    if (expandedId === deleteConfirmId) setExpandedId(null);
    setDeleteConfirmId(null);
    setDeleteSource(null);
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const generateHookName = (session, discussion) => {
    const parts = [];

    // Hauptdiagnose
    if (session.diagnoses && Array.isArray(session.diagnoses) && session.diagnoses.length > 0) {
      parts.push(session.diagnoses[0].name);
    }

    // Hauptsymptome aus der ersten Summary-Sektion
    if (session.summary && Array.isArray(session.summary) && session.summary.length > 0) {
      const firstItems = session.summary[0].items || [];
      const keywords = firstItems.slice(0, 2).map((item) => {
        // Kürze lange Symptome auf die ersten paar Wörter
        const words = item.split(' ');
        return words.length > 4 ? words.slice(0, 4).join(' ') : item;
      });
      if (keywords.length > 0) {
        parts.push(keywords.join(', '));
      }
    }

    // Hauptfrage aus den Diskussionspunkten (erster Satz)
    if (discussion && discussion.trim()) {
      const firstSentence = discussion.trim().split(/[.?!]\s/)[0].trim();
      const short = firstSentence.length > 60 ? firstSentence.substring(0, 57) + '...' : firstSentence;
      parts.push(short.endsWith('?') ? short : short + '?');
    }

    return parts.join(' \u2013 ') || session.name;
  };

  const handleShareClick = (e, session) => {
    e.stopPropagation();
    setShareSession(session);
    setDiscussionPoints('');
  };

  const confirmShare = () => {
    if (shareSession) {
      const hookName = generateHookName(shareSession, discussionPoints);
      shareHook(shareSession, discussionPoints, hookName);
      setShareSession(null);
      setDiscussionPoints('');
    }
  };

  return (
    <div className="page page-wide">
      <h1 className="page-title">Konsultationsarchiv</h1>
      <p className="page-subtitle">Abgeschlossene Konsultationen</p>

      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          className="settings-select"
          style={{ minWidth: '180px' }}
          value={patientFilter}
          onChange={(e) => setPatientFilter(e.target.value)}
        >
          <option value="">Alle Patienten</option>
          {patients.map((p) => (
            <option key={p.id} value={p.patient_label}>{p.patient_label}</option>
          ))}
        </select>
        {patientFilter && (
          <span style={{ fontSize: '13px', color: '#6b7280' }}>
            {backendSessions.length} Konsultation{backendSessions.length !== 1 ? 'en' : ''} für {patientFilter}
          </span>
        )}
        <button
          onClick={() => setRefreshKey(k => k + 1)}
          style={{ marginLeft: 'auto', padding: '7px 14px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', color: '#374151' }}
          title="Konsultationen neu laden"
        >
          ↻ Aktualisieren
        </button>
      </div>

      {backendError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', color: '#991b1b', fontSize: 13, marginBottom: 16 }}>
          <strong>Server-Fehler:</strong> {backendError}
          {backendError.includes('401') ? ' — Bitte neu einloggen.' : ''}
        </div>
      )}

      {sessions.length === 0 ? (
        <div className="session-list-empty">
          <p>Keine archivierten Konsultationen vorhanden.</p>
          <p className="session-list-empty-hint">Abgeschlossene Konsultationen werden hier archiviert.</p>
        </div>
      ) : (
        <div className="session-list">
          {sessions.map((session) => (
            <div
              key={session.id}
              className={`session-list-card session-list-card-archived${expandedId === session.id ? ' expanded' : ''}`}
              onClick={() => toggleExpand(session.id)}
            >
              <div className="session-list-card-header">
                <h3 className="session-list-card-name">
                  {session.session_name || session.name}
                  {session.patient_label && (
                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>
                      · {session.patient_label}
                    </span>
                  )}
                </h3>
                <span className="session-list-card-date">
                  {formatDate(session.session_date || session.updatedAt)}
                </span>
              </div>
              <p className="session-list-card-preview">{getPreview(session)}</p>

              {expandedId === session.id && (
                <div className="session-list-card-detail">
                  {session.summary && Array.isArray(session.summary) && session.summary.length > 0 && (
                    <div className="session-detail-section">
                      <h4>Zusammenfassung</h4>
                      {session.summary.map((section, i) => (
                        <div key={i} className="session-detail-summary-section">
                          <strong>{section.title}</strong>
                          <ul>
                            {(section.items || []).map((item, j) => (
                              <li key={j}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {session.diagnoses && Array.isArray(session.diagnoses) && session.diagnoses.length > 0 && (
                    <div className="session-detail-section">
                      <h4>Differentialdiagnosen</h4>
                      {session.diagnoses.map((d, i) => (
                        <div key={i} className="session-detail-diagnosis">
                          <strong>{d.name}</strong>
                          <p>{d.reason}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {session.manualNotes && (
                    <div className="session-detail-section">
                      <h4>Ergänzungen</h4>
                      <p>{session.manualNotes}</p>
                    </div>
                  )}

                  {(session.privateNotes || session.private_notes) && (
                    <div className="session-detail-section" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 14px' }}>
                      <h4 style={{ color: '#92400e', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        Interne Notizen
                        <span style={{ fontSize: 11, fontWeight: 600, background: '#fef3c7', color: '#b45309', padding: '2px 7px', borderRadius: 10, letterSpacing: '0.02em' }}>
                          Nur für Sie
                        </span>
                      </h4>
                      <p style={{ whiteSpace: 'pre-wrap', color: '#78350f', fontSize: 13 }}>{session.privateNotes || session.private_notes}</p>
                    </div>
                  )}

                  {(() => {
                    const docs = session.uploaded_docs || session.uploadedDocs || [];
                    if (!docs.length) return null;
                    const catLabel = { pdf: 'PDF', docx: 'Word', image: 'Bild', text: 'Text', other: 'Datei' };
                    return (
                      <div className="session-detail-section">
                        <h4>Befunddokumente</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 4 }}>
                          {docs.map((doc, i) => (
                            <div key={doc.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 7 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, background: '#eef2ff', color: '#4f46e5', padding: '1px 7px', borderRadius: 10, flexShrink: 0 }}>
                                {catLabel[doc.category] || 'Datei'}
                              </span>
                              <span style={{ fontSize: 13, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {doc.name}
                              </span>
                              <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>
                                {doc.size < 1024 ? `${doc.size} B` : doc.size < 1048576 ? `${Math.round(doc.size / 1024)} KB` : `${(doc.size / 1048576).toFixed(1)} MB`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="session-list-card-actions">
                {isShared(session.id) ? (
                  <button className="session-list-btn-share" disabled onClick={(e) => e.stopPropagation()}>
                    Geteilt
                  </button>
                ) : (
                  <button className="session-list-btn-share" onClick={(e) => handleShareClick(e, session)}>
                    Fallbeitrag teilen
                  </button>
                )}
                <button className="session-list-btn-delete" onClick={(e) => handleDeleteClick(e, session)}>
                  Löschen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteConfirmId && (
        <div className="session-modal-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="session-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Konsultation löschen</h3>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '16px' }}>
              Möchten Sie diese Konsultation wirklich unwiderruflich löschen?
            </p>
            <div className="session-modal-actions">
              <button className="modal-btn-cancel" onClick={() => setDeleteConfirmId(null)}>
                Abbrechen
              </button>
              <button className="modal-btn-confirm" onClick={confirmDelete} style={{ background: '#dc2626' }}>
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {shareSession && (
        <div className="session-modal-overlay" onClick={() => setShareSession(null)}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <h3>Fallbeitrag teilen</h3>
              <button className="report-close-btn" onClick={() => setShareSession(null)} title="Schließen">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="share-modal-preview">
              <div className="share-modal-preview-label">Konsultation</div>
              <h4 className="share-modal-preview-name">{shareSession.name}</h4>
              <p className="share-modal-preview-text">{getPreview(shareSession)}</p>
              {shareSession.diagnoses && Array.isArray(shareSession.diagnoses) && shareSession.diagnoses.length > 0 && (
                <div className="share-modal-preview-diagnoses">
                  {shareSession.diagnoses.map((d, i) => (
                    <span key={i} className="share-modal-diagnosis-tag">{d.name}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="share-modal-discussion">
              <label className="share-modal-discussion-label">Diskussionspunkte</label>
              <textarea
                className="share-modal-discussion-input"
                placeholder="Welche Fragen haben Sie an die Community?"
                value={discussionPoints}
                onChange={(e) => setDiscussionPoints(e.target.value)}
              />
            </div>

            <div className="share-modal-actions">
              <button className="modal-btn-cancel" onClick={() => setShareSession(null)}>
                Abbrechen
              </button>
              <button className="share-modal-btn-confirm" onClick={confirmShare}>
                Beitrag teilen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SessionArchivPage;
